// ============================================================
// BillEmailConsentCard.tsx — B3: the client's consent to receive BILLS BY
// EMAIL at one exact address (client page + the case's Client details).
// Self-loading: GET/PUT/DELETE /api/clients/:id/bill-email-consent (audited
// server-side). Bills can be emailed only to the consented address
// (invoiceController sendBill); otherwise the bill goes by post / by hand.
// When the client's email has changed since consent → "Consent was given for
// [old]. Record new consent to email bills to [new]."
// (Batch 2) The amber notes (no consent yet / email changed) are ActionBoxes
// — the message, then the real buttons. On a case (caseId), a record /
// remove announces 'clientDetails' so Settlement re-reads. Not shown on a
// case whose client is a child / protected party (the litigation friend's
// consent applies — ClientDetailsSection).
// ============================================================
import React, { useEffect, useState } from 'react'
import { getBillEmailConsentApi, recordBillEmailConsentApi, removeBillEmailConsentApi, type BillEmailConsentState } from '../../api/casevoApi'
import FormField from '../shared/FormField'
import FieldError from '../shared/FieldError'
import DateInput from '../shared/DateInput'
import ActionBox from '../shared/ActionBox'
import ConsentEditCard from '../billing/ConsentEditCard'
import { emitCaseChange, useCaseChange } from '../../utils/caseChangeEvents'
import { BIcon, B_SECONDARY_SM, CARD, CardHead, INPUT, NOTE, errorText, fmtDate } from '../billing/billingUi'
import { NO_EMAIL_ON_FILE, consentChangedMessage } from '../../utils/noEmail'
import { EMAIL_FORMAT_ERROR, isValidEmail } from '../../utils/contactFormat'
import { londonToday } from '../../utils/civilDate'

// Same keys + labels as backend lib/billDelivery CONSENT_METHODS.
export const CONSENT_METHOD_OPTIONS: { value: string; label: string }[] = [
  { value: 'CLIENT_CARE_LETTER', label: 'Client care letter' },
  { value: 'SIGNED_FORM', label: 'Signed form' },
  { value: 'EMAIL_REPLY', label: 'Email reply' },
  { value: 'VERBAL_RECORDED', label: 'Verbal (recorded)' },
  { value: 'OTHER', label: 'Other' },
]
const methodLabel = (m: string | null): string => CONSENT_METHOD_OPTIONS.find((o) => o.value === m)?.label ?? 'Not stated'
const norm = (s: string | null | undefined): string => (s ?? '').trim().toLowerCase()

// Pure (exported for tests): the changed-email prompt, or null.
export const consentPrompt = (c: Pick<BillEmailConsentState, 'billEmailConsentAt' | 'billEmailConsentEmail' | 'emailAddress' | 'hasEmailAddress'>): string | null => {
  if (!c.billEmailConsentAt || !c.billEmailConsentEmail || c.hasEmailAddress === false) return null
  if (!c.emailAddress.trim() || norm(c.emailAddress) === norm(c.billEmailConsentEmail)) return null
  return consentChangedMessage(c.billEmailConsentEmail, c.emailAddress.trim())
}

// currentEmail / currentNoEmail: the record as it is on screen now (it may be
// edited after this card loaded); the saved values otherwise.
const BillEmailConsentCard: React.FC<{ clientId: string; caseId?: string; clientName?: string; currentEmail?: string; currentNoEmail?: boolean }> = ({ clientId, caseId, clientName, currentEmail, currentNoEmail }) => {
  const [state, setState] = useState<BillEmailConsentState | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  // B3c — Casevo's DateInput (DD / MM / YYYY), not the browser's date box.
  const [date, setDate] = useState<string | null>(londonToday())
  const [dateTyping, setDateTyping] = useState(false)
  const [email, setEmail] = useState('')
  const [method, setMethod] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<{ field?: string; text: string } | null>(null)

  // (D12) Someone else recorded / removed the consent → re-read (not over an open form).
  const [rev, setRev] = useState(0)
  useCaseChange(caseId, ['clientDetails'], (_k, info) => { if (info.remote && !editing) setRev((n) => n + 1) })
  useEffect(() => {
    let live = true
    getBillEmailConsentApi(clientId)
      .then((s) => { if (live) setState(s) })
      .catch((e) => { if (live) setLoadErr(errorText(e, 'Couldn’t load the bill email consent.')) })
    return () => { live = false }
  }, [clientId, rev])

  const startRecord = (): void => {
    setDate(londonToday()); setEmail((currentEmail ?? state?.emailAddress ?? '').trim()); setMethod(''); setErr(null); setEditing(true)
  }
  const save = async (): Promise<void> => {
    if (busy || dateTyping) return
    if (!isValidEmail(email)) { setErr({ field: 'email', text: EMAIL_FORMAT_ERROR }); return }
    if (!date) { setErr({ field: 'date', text: 'Enter the date the client agreed.' }); return }
    setBusy(true); setErr(null)
    try { setState(await recordBillEmailConsentApi(clientId, { date, email: email.trim(), method })); setEditing(false); if (caseId) emitCaseChange(caseId, 'clientDetails') }
    catch (e) {
      const field = (e as { response?: { data?: { field?: string } } })?.response?.data?.field
      setErr({ field, text: errorText(e, 'Couldn’t record the consent.') })
    } finally { setBusy(false) }
  }
  // Cancel → back to the notice, focus on its "Record consent".
  const cancelRecord = (): void => {
    setEditing(false); setErr(null)
    window.setTimeout(() => document.getElementById(`bec-record-${clientId}`)?.focus(), 0)
  }
  const remove = async (): Promise<void> => {
    if (busy) return
    setBusy(true); setErr(null)
    try { setState(await removeBillEmailConsentApi(clientId)); if (caseId) emitCaseChange(caseId, 'clientDetails') }
    catch (e) { setErr({ text: errorText(e, 'Couldn’t remove the consent.') }) } finally { setBusy(false) }
  }

  // M10 — a field's error is FieldError, tied to its input with aria-describedby.
  const errId = (f: string): string => `bec-${f}-err-${clientId}`
  const fieldErr = (f: string): string | undefined => (err?.field === f ? err.text : undefined)
  const described = (f: string) => ({ 'aria-invalid': !!fieldErr(f) || undefined, 'aria-describedby': fieldErr(f) ? errId(f) : undefined })
  const who = clientName?.trim() || 'The client'
  const live = state && { ...state, emailAddress: currentEmail ?? state.emailAddress, hasEmailAddress: currentNoEmail === undefined ? state.hasEmailAddress : currentNoEmail ? false : state.hasEmailAddress === false ? null : state.hasEmailAddress }
  const prompt = live ? consentPrompt(live) : null
  const noEmail = live?.hasEmailAddress === false
  return (
    <section aria-label="Bills by email" className={`${CARD} p-[18px] mt-4`}>
      <CardHead icon="mail" title="Bills by email" sub="Bills are emailed only to the address the client agreed to. Otherwise send them by post or hand them over." />
      {loadErr && <p role="alert" className="m-0 mt-2 text-[13px] text-casevo-danger">{loadErr}</p>}
      {state && !editing && (
        <div className="mt-3 grid gap-3">
          {state.billEmailConsentAt
            ? <dl className="m-0 grid grid-cols-[140px_1fr] gap-x-3 gap-y-[6px] text-[13.5px]">
              <dt className="text-casevo-text-soft">Agreed address</dt><dd className="m-0 font-semibold text-casevo-forest break-all">{state.billEmailConsentEmail}</dd>
              <dt className="text-casevo-text-soft">Date agreed</dt><dd className="m-0 text-casevo-forest">{fmtDate(state.billEmailConsentAt, 'long')}</dd>
              <dt className="text-casevo-text-soft">How</dt><dd className="m-0 text-casevo-forest">{methodLabel(state.billEmailConsentMethod)}</dd>
              {state.recordedByName && <><dt className="text-casevo-text-soft">Recorded by</dt><dd className="m-0 text-casevo-forest">{state.recordedByName}</dd></>}
            </dl>
            : noEmail
              ? <p className={`${NOTE} m-0`}>{NO_EMAIL_ON_FILE}. Bills go by post or by hand.</p>
              : <ActionBox ariaLabel="Bill email consent needed" title="Consent needed to email bills" message={`${who} hasn’t agreed to receive bills by email. Until they do, send bills by post or by hand.`}
                actions={[{ label: 'Record consent', kind: 'primary', onClick: startRecord, disabled: busy, id: `bec-record-${clientId}` }]} />}
          {prompt && (
            <ActionBox ariaLabel="Client email changed" title="The client’s email has changed" message={prompt}
              actions={[
                { label: 'Remove consent', kind: 'link', onClick: () => { void remove() }, disabled: busy },
                { label: 'Record new consent', kind: 'primary', onClick: startRecord, disabled: busy, id: `bec-record-${clientId}` },
              ]} />
          )}
          {state.billEmailConsentAt && !prompt && (
            <div className="flex flex-wrap gap-2">
              {!noEmail && <button type="button" className={B_SECONDARY_SM} disabled={busy} onClick={startRecord}><BIcon name="plus" size={12} />Record new consent</button>}
              <button type="button" className={B_SECONDARY_SM} disabled={busy} onClick={() => { void remove() }}>Remove consent</button>
            </div>
          )}
        </div>
      )}
      {state && editing && (
        <div className="mt-3 flex flex-col gap-[10px]">
        {/* (Audit fix batch 2) The notice shrinks to one line; a clean edit card opens beneath. */}
        <ActionBox tone="warn" variant="slim" title={prompt ? 'The client’s email has changed' : 'Consent needed to email bills'} status="· recording…" />
        <ConsentEditCard context={<>{who} · bills by email</>} focusId={`bec-email-${clientId}`} busy={busy} saveLabel="Save consent"
          onSave={() => { void save() }} onCancel={cancelRecord} footNote="Kept on the client record." error={err && !err.field ? err.text : null}>
          <FormField label="Exact email address agreed" required variant="info" htmlFor={`bec-email-${clientId}`}>
            <input id={`bec-email-${clientId}`} type="email" className={INPUT} value={email} maxLength={200} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" {...described('email')} />
            <span id={errId('email')}><FieldError message={fieldErr('email')} /></span>
          </FormField>
          <FormField label="Date agreed" required variant="info">
            <DateInput value={date} onChange={(v) => { setDate(v); if (err?.field === 'date') setErr(null) }} ariaLabel="Date agreed" onValidityChange={(v) => setDateTyping(!v)}
              describedBy={fieldErr('date') ? errId('date') : undefined} invalid={!!fieldErr('date')} />
            <span id={errId('date')}><FieldError message={fieldErr('date')} /></span>
          </FormField>
          <FormField label="How the client agreed" required variant="info" htmlFor={`bec-method-${clientId}`}>
            <select id={`bec-method-${clientId}`} className={INPUT} value={method} onChange={(e) => setMethod(e.target.value)} {...described('method')}>
              <option value="">Choose…</option>
              {CONSENT_METHOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <span id={errId('method')}><FieldError message={fieldErr('method')} /></span>
          </FormField>
        </ConsentEditCard>
        </div>
      )}
      {err && !editing && <p role="alert" className="m-0 mt-2 text-[13px] text-casevo-danger">{err.text}</p>}
    </section>
  )
}

export default BillEmailConsentCard
